import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Responsive, WidthProvider, type Layout, type Layouts } from "react-grid-layout";
import { Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ArrowUpCircle, Copy, Eye, LayoutGrid, Mail, MoreHorizontal, Plus, Sparkles, Store, Trash2, Users, X, ZoomIn } from "lucide-react";
import {
  appendWidget, dashboardInputs, GRID_COLS, isStaff, type DashboardBody, type InputValues, type InstalledFrom, type LayoutItem, type ListingDetail, type ListingKind, type SemanticQueryInput, type UpdateTarget, type Widget,
} from "@cd/core";
import { capabilities } from "../../lib/access";
import { api, ApiError, type DashboardDetail } from "../../lib/api";
import { DATES_V, type DateZoom } from "../../lib/dateZoom";
import type { Drill } from "../../lib/drill";
import { rememberDashboard } from "../../lib/lastDashboard";
import { addPageFilter, describePageFilter, type PageFilter } from "../../lib/pageFilters";
import { asSelection, type TimeSelection } from "../../lib/time";
import { Button } from "../../components/ui/button";
import { Menu, MenuContent, MenuItem, MenuTrigger } from "../../components/ui/overlay";
import { Badge, Kbd, Skeleton } from "../../components/ui/primitives";
import { Builder } from "../builder/Builder";
import { draftFromDefinition, useBuilder } from "../builder/store";
import { AskBar } from "./AskBar";
import { BreakdownPanel } from "./BreakdownPanel";
import { DeleteDashboardDialog } from "./DeleteDashboardDialog";
import { FilterBar } from "./FilterBar";
import { InputsBar } from "./InputsBar";
import { effectiveInputs } from "../../lib/inputs";
import { PublishDialog } from "../marketplace/PublishDialog";
import { UpdateDialog } from "../marketplace/UpdateDialog";
import { DashboardSchedulesDialog } from "../schedules/ScheduleDialog";
import { ShareDialog } from "./ShareDialog";
import { useShareResponse } from "./shareResponse";
import { WidgetCard } from "./WidgetCard";

const Grid = WidthProvider(Responsive);
const BREAKPOINTS = { lg: 1100, md: 700, sm: 0 };

const toItems = (l: Layout[]): LayoutItem[] => l.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }));
const outdated = (f?: InstalledFrom) => f?.latestVersion !== undefined && f.latestVersion > f.version;

export function DashboardPage({ id, pageFilters, onPageFiltersChange, zoom, onZoomChange, drill, onDrillChange, triedInputs, onTriedInputsChange }: {
  id: string;
  /** Filters from clicking values on this page (the URL's `f`), never saved with the dashboard. */
  pageFilters: PageFilter[];
  onPageFiltersChange: (f: PageFilter[]) => void;
  /** A date bucket clicked on this page (the URL's `z`): its dates replace the saved range until cleared. */
  zoom?: DateZoom;
  onZoomChange: (z: DateZoom | undefined) => void;
  /** "Break down … by…" (the URL's `d`): a side panel, page state like the chips. */
  drill?: Drill;
  onDrillChange: (d: Drill | undefined, opts?: { replace?: boolean }) => void;
  /** Input values this viewer is trying instead of the saved ones (the URL's `in`), never saved. */
  triedInputs?: InputValues;
  onTriedInputsChange: (v: InputValues | undefined) => void;
}) {
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ["dashboard", id], queryFn: () => api.dashboard(id) });
  const [body, setBody] = useState<DashboardBody | null>(null);
  const [widgets, setWidgets] = useState<Record<string, Widget>>({});
  const [askOpen, setAskOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [publishing, setPublishing] = useState<{ kind: ListingKind; sourceId: string; title: string; description?: string; queries: SemanticQueryInput[] }>();
  const [publishOpen, setPublishOpen] = useState(false);
  const [justPublished, setJustPublished] = useState<ListingDetail>();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [updating, setUpdating] = useState<{ listingId: string; target: UpdateTarget; title: string }>();
  const [updateOpen, setUpdateOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // Which of their locations this viewer is looking at. Only ever page state: a dashboard saves its
  // date range, never locations, so it shows each viewer their own.
  const [locationIds, setLocationIds] = useState<string[]>();
  useEffect(() => setLocationIds(undefined), [id]);
  const navigate = useNavigate();
  const openBuilder = useBuilder((s) => s.openBuilder);

  // The revision our edits are based on; every save sends it and moves it on.
  const revision = useRef(0);
  const [conflict, setConflict] = useState(false);

  useEffect(() => {
    if (!detail.data) return;
    const { dashboard: d, widgets: w } = detail.data;
    setBody({ title: d.title, items: d.items.filter((i) => w[i.widgetId]), layout: d.layout, globalFilters: d.globalFilters, inputs: d.inputs });
    setWidgets(w);
    revision.current = d.revision;
    setConflict(false);
  }, [detail.data]);

  // The server says whether this viewer may edit (the owner; while viewing as them, only in edit mode).
  const canEdit = detail.data?.canEdit ?? false;
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, staleTime: Infinity });
  const can = capabilities(me.data);
  // Staff, or customers a super user turned Ask on for (the worker checks the same on /nl). It opens
  // the builder, so it also needs edit access here.
  const canAsk = canEdit && Boolean(me.data?.canAsk);
  // Publishing to the marketplace is staff-only for now (server-enforced on POST /marketplace), and never on someone's behalf.
  const canPublish = canEdit && can.own && (me.data ? isStaff(me.data) : false);
  const canUpdateCopies = canEdit && can.install;
  // Scheduled report emails: staff only for now, never while viewing as someone (viewAsTier is unset
  // then), on any dashboard they can open. The worker enforces the same (schedules.ts).
  const canSchedule = Boolean(me.data?.viewAsTier);
  const myListings = useQuery({ queryKey: ["marketplace-mine"], queryFn: api.myListings, enabled: canEdit });
  const listing = myListings.data?.find((l) => l.kind === "dashboard" && l.sourceId === id);
  // Where Home lands next time. Not while viewing as someone: their dashboards aren't where staff
  // want to land back on their own.
  const opened = Boolean(detail.data);
  const userId = me.data?.userId;
  useEffect(() => {
    if (opened && userId && !can.viewAs) rememberDashboard(userId, id);
  }, [opened, userId, can.viewAs, id]);

  // Debounced autosave of layout/filters/title for the owner. Saves go one at a time, each from
  // the revision the last one returned; if someone else saved in between, stop and say so.
  const saveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const unsaved = useRef<DashboardBody | null>(null);
  const saving = useRef(false);
  const save = useMutation({
    mutationFn: (b: DashboardBody) => api.saveDashboard(id, b, revision.current),
    onSuccess: (saved) => {
      revision.current = saved.dashboard.revision;
      void qc.invalidateQueries({ queryKey: ["dashboards"] });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) setConflict(true);
    },
  });
  const { mutateAsync: saveNow } = save;
  const flush = useCallback(async () => {
    if (saving.current) return;
    saving.current = true;
    try {
      for (let b = unsaved.current; b; b = unsaved.current) {
        unsaved.current = null;
        await saveNow(b);
      }
    } catch {
      unsaved.current = null; // shown via save.error / the conflict banner
    } finally {
      saving.current = false;
    }
  }, [saveNow]);
  const persist = useCallback(
    (b: DashboardBody) => {
      setBody(b);
      if (!canEdit || conflict) return;
      unsaved.current = b;
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => void flush(), 600);
    },
    [canEdit, conflict, flush],
  );

  const respond = useShareResponse();
  const copyToMine = useMutation({
    mutationFn: () => api.duplicateDashboard(id),
    onSuccess: (copy) => {
      qc.invalidateQueries({ queryKey: ["dashboards"] });
      void navigate({ to: "/d/$id", params: { id: copy.dashboard.id } });
    },
  });

  const { timePreset, timeRange } = body?.globalFilters ?? {};
  const saved = useMemo(
    () => asSelection({ timePreset, timeRange: timeRange && { start: timeRange.start, end: timeRange.end } }),
    [timePreset, timeRange?.start, timeRange?.end],
  );
  const time = useMemo<TimeSelection>(() => (zoom ? { timeRange: zoom.range } : saved), [zoom?.range.start, zoom?.range.end, saved]);
  // One box per input id the widgets use; the values they run with are the saved ones or this viewer's.
  const inputBoxes = useMemo(
    () => (body ? dashboardInputs(body.items, (wid) => widgets[wid], body.inputs) : []),
    [body?.items, body?.inputs, widgets],
  );
  const tried = useMemo(() => {
    const ids = new Set(inputBoxes.map((i) => i.id));
    return Object.fromEntries(Object.entries(triedInputs ?? {}).filter(([k]) => ids.has(k)));
  }, [inputBoxes, triedInputs]);
  const inputValues = useMemo(() => effectiveInputs(inputBoxes, tried), [inputBoxes, tried]);
  const tryInput = (inputId: string, v: number | undefined) => {
    const { [inputId]: _, ...rest } = tried;
    onTriedInputsChange(v === undefined ? rest : { ...rest, [inputId]: v });
  };
  // Allowed grains of each time field, so a zoom steps axes only to grains the field has. Ask needs it too.
  const catalog = useQuery({ queryKey: ["catalog"], queryFn: api.catalog, staleTime: 5 * 60_000, enabled: !!zoom || canAsk });
  // Ask reads the workspace's first data pack, so it can only change that pack's widgets.
  const firstPack = catalog.data?.packs[0]?.name;
  const grainsOf = useMemo(() => {
    // Without a catalog, axes keep their grain rather than leaving widgets waiting.
    if (catalog.isError) return () => undefined;
    const packs = catalog.data?.packs;
    if (!packs) return undefined;
    return (pack: string | undefined, ref: string) =>
      (packs.find((p) => p.name === (pack ?? "")) ?? packs[0])?.dimensions.find((d) => d.ref === ref)?.grains;
  }, [catalog.data, catalog.isError]);

  const addWidget = (w: Widget, isNew: boolean) => {
    setWidgets((ws) => ({ ...ws, [w.id]: w }));
    qc.invalidateQueries({ queryKey: ["query"] });
    if (!isNew || !body) return;
    persist(appendWidget(body, w.id, w.definition.visualization.type));
  };

  const removeItem = (i: string) =>
    body &&
    persist({
      ...body,
      items: body.items.filter((x) => x.i !== i),
      layout: { lg: body.layout.lg.filter((l) => l.i !== i), md: body.layout.md.filter((l) => l.i !== i), sm: body.layout.sm.filter((l) => l.i !== i) },
    });

  const duplicate = async (w: Widget) => {
    const copy = await api.duplicateWidget(w.id, `${w.definition.title.slice(0, 113)} (copy)`);
    addWidget(copy, true);
  };

  if (detail.error) return <p className="p-8 text-bad">{(detail.error as Error).message}</p>;
  if (detail.isLoading || !body) return <DashboardSkeleton />;
  const { dashboard: d, shares = [] } = detail.data as DashboardDetail;
  const from = d.installedFrom;
  const startUpdate = (u: NonNullable<typeof updating>) => {
    // Save pending edits first, so the preview (and its warning) sees them.
    clearTimeout(saveTimer.current);
    void flush();
    setUpdating(u);
    setUpdateOpen(true);
  };
  const publish = (p: NonNullable<typeof publishing>) => {
    setPublishing(p);
    setPublishOpen(true);
  };
  // Installed copies are someone else's work, so only the original can be published.
  const installedWidget = body.items.map((i) => widgets[i.widgetId]).find((w) => w?.installedFrom);
  const publishBlocked = from
    ? "Installed from the marketplace, so it can't be published again"
    : installedWidget
      ? `“${installedWidget.definition.title}” was installed from the marketplace; remove it to publish this dashboard`
      : !body.items.length
        ? "Add a widget before publishing"
        : undefined;

  return (
    <div className="mx-auto max-w-[1600px] px-6 pb-16">
      <header className="sticky top-0 z-20 -mx-6 mb-4 flex flex-wrap items-center gap-3 border-b border-border bg-background/85 px-6 py-3 backdrop-blur">
        {/* A real basis, not flex-1's 0%, so a narrow header wraps the controls below the title instead of squeezing it. */}
        <div className="min-w-0 flex-[1_1_10rem]">
          {canEdit ? (
            <input
              value={body.title}
              onChange={(e) => persist({ ...body, title: e.target.value })}
              className="w-full bg-transparent text-lg font-semibold tracking-tight outline-none"
              aria-label="Dashboard title"
            />
          ) : (
            <h1 className="truncate text-lg font-semibold tracking-tight">{body.title}</h1>
          )}
          {d.sharedBy && (
            <p className="flex flex-wrap items-center gap-1 text-xs text-muted">
              <Eye className="size-3" /> Shared by {d.sharedBy} · view only · showing your locations
              {can.write && (
                <>
                  {" · "}
                  <button
                    disabled={respond.isPending}
                    onClick={() => respond.mutate({ id, status: d.shareStatus === "accepted" ? "pending" : "accepted" })}
                    className="font-medium text-accent hover:underline disabled:opacity-40"
                  >
                    {d.shareStatus === "accepted" ? "Remove from sidebar" : "Add to sidebar"}
                  </button>
                </>
              )}
              {respond.error && <span className="text-bad">{respond.error.message}</span>}
            </p>
          )}
          {from && !outdated(from) && (
            <p className="flex items-center gap-1 text-xs text-muted">
              <Store className="size-3" /> Installed from the{" "}
              <Link to="/marketplace/$id" params={{ id: from.listingId }} className="hover:text-foreground hover:underline">marketplace</Link>
              {" "}· v{from.version}{from.latestVersion !== undefined && " (latest)"}
            </p>
          )}
          {listing && (
            <p className="flex items-center gap-1 text-xs text-muted">
              <Store className="size-3" /> Published to the{" "}
              <Link to="/marketplace/$id" params={{ id: listing.id }} className="hover:text-foreground hover:underline">marketplace</Link>
              {" "}· v{listing.version}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <FilterBar
            filters={body.globalFilters}
            onChange={(f) => {
              // A new range replaces the zoomed one rather than hiding behind it.
              if (zoom) onZoomChange(undefined);
              persist({ ...body, globalFilters: f });
            }}
            locationIds={locationIds}
            onLocationsChange={setLocationIds}
            zoomed={!!zoom}
          />
          {!canEdit && can.write && (
            <Button disabled={copyToMine.isPending} onClick={() => copyToMine.mutate()}>
              <Copy className="size-3.5" /> Duplicate to my dashboards
            </Button>
          )}
          {copyToMine.error && <span className="text-xs text-bad">{copyToMine.error.message}</span>}
          {canEdit && (
            <>
              {can.own && (
                <Button onClick={() => setShareOpen(true)}>
                  <Users className="size-3.5" /> {shares.length ? `Shared · ${shares.length}` : "Share"}
                </Button>
              )}
              {canPublish && (
                <Button
                  disabled={!!publishBlocked}
                  title={publishBlocked}
                  onClick={() => publish({
                    kind: "dashboard", sourceId: id, title: body.title,
                    queries: body.items.flatMap((i) => (widgets[i.widgetId] ? [widgets[i.widgetId]!.definition.query] : [])),
                  })}
                >
                  <Store className="size-3.5" /> {listing ? "Publish update" : "Publish"}
                </Button>
              )}
              {canAsk && (
                <Button onClick={() => setAskOpen(true)}>
                  <Sparkles className="size-3.5 text-accent" /> Ask <Kbd>⌘K</Kbd>
                </Button>
              )}
              <Button variant="primary" onClick={() => openBuilder()}>
                <Plus className="size-3.5" /> Add widget
              </Button>
            </>
          )}
          {(canEdit || canSchedule) && (
            <Menu>
              <MenuTrigger asChild>
                <Button size="icon" variant="ghost" aria-label="Dashboard menu"><MoreHorizontal className="size-4" /></Button>
              </MenuTrigger>
              <MenuContent>
                {canSchedule && <MenuItem onSelect={() => setScheduleOpen(true)}><Mail className="size-4" /> Schedule email</MenuItem>}
                {canEdit && <MenuItem danger onSelect={() => setDeleteOpen(true)}><Trash2 className="size-4" /> Delete dashboard</MenuItem>}
              </MenuContent>
            </Menu>
          )}
          {save.isPending && <Badge>Saving…</Badge>}
          {save.error && !conflict && <span className="text-xs text-bad">Couldn't save: {save.error.message}</span>}
        </div>
        <InputsBar
          inputs={inputBoxes}
          tried={tried}
          canEdit={canEdit}
          onSave={(inputId, v) => {
            // The owner's value is the saved one; one they were trying in the URL gives way to it.
            if (inputId in tried) tryInput(inputId, undefined);
            // Values for ids no widget declares any more are dropped, so they can't pile up.
            const kept = Object.fromEntries(inputBoxes.filter((i) => body.inputs && i.id in body.inputs).map((i) => [i.id, body.inputs![i.id]!]));
            persist({ ...body, inputs: { ...kept, [inputId]: v } });
          }}
          onTry={tryInput}
        />
        {(pageFilters.length > 0 || zoom) && (
          <div className="flex basis-full flex-wrap items-center gap-1.5">
            {zoom && (
              <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-accent-soft py-0.5 pr-1 pl-2 text-xs text-accent">
                <ZoomIn className="size-3 shrink-0" />
                <span className="truncate">Zoomed to {zoom.label}</span>
                <button onClick={() => onZoomChange(undefined)} aria-label={`Remove zoom: ${zoom.label}`} className="rounded p-0.5 hover:bg-accent/10">
                  <X className="size-3" />
                </button>
              </span>
            )}
            {pageFilters.map((c, i) => (
              <span key={i} className="inline-flex max-w-full items-center gap-1 rounded-md bg-accent-soft py-0.5 pr-1 pl-2 text-xs text-accent">
                <span className="truncate">{describePageFilter(c)}</span>
                <button
                  onClick={() => onPageFiltersChange(pageFilters.filter((_, j) => j !== i))}
                  aria-label={`Remove filter: ${describePageFilter(c)}`}
                  className="rounded p-0.5 hover:bg-accent/10"
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
            {pageFilters.length + (zoom ? 1 : 0) > 1 && (
              <button onClick={() => { onPageFiltersChange([]); onZoomChange(undefined); }} className="px-1 text-xs text-muted hover:text-foreground hover:underline">Clear all</button>
            )}
          </div>
        )}
      </header>

      {conflict && (
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-warn-bg px-3.5 py-2.5 text-sm text-warn-fg">
          <AlertTriangle className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="font-medium">This dashboard was changed somewhere else</span> (another tab, an assistant, or a marketplace install), so your latest edits weren't saved.
          </span>
          <button onClick={() => void detail.refetch()} className="font-medium hover:underline">Reload latest</button>
        </div>
      )}

      {canUpdateCopies && from && outdated(from) && (
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-warn-bg px-3.5 py-2.5 text-sm text-warn-fg">
          <ArrowUpCircle className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="font-medium">An update is available.</span> You have v{from.version} from the marketplace; v{from.latestVersion} is out.
          </span>
          <Link to="/marketplace/$id" params={{ id: from.listingId }} className="font-medium hover:underline">See the listing</Link>
          <Button size="sm" onClick={() => startUpdate({ listingId: from.listingId, target: { dashboardId: id }, title: body.title })}>
            Update to v{from.latestVersion}
          </Button>
        </div>
      )}
      {justPublished && (
        <div className="mb-4 flex items-center gap-3 rounded-lg bg-accent-soft px-3.5 py-2.5 text-sm text-accent">
          <Store className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">Published “{justPublished.title}” v{justPublished.version} to the marketplace.</span>
          <Link to="/marketplace/$id" params={{ id: justPublished.id }} className="font-medium hover:underline">View listing</Link>
          <button onClick={() => setJustPublished(undefined)} aria-label="Dismiss" className="rounded p-0.5 hover:bg-accent/10"><X className="size-3.5" /></button>
        </div>
      )}

      {body.items.length === 0 ? (
        <EmptyDashboard canEdit={canEdit} onAdd={() => openBuilder()} onAsk={canAsk ? () => setAskOpen(true) : undefined} />
      ) : (
        <Grid
          className="-mx-2"
          layouts={body.layout as unknown as Layouts}
          breakpoints={BREAKPOINTS}
          cols={GRID_COLS}
          rowHeight={32}
          margin={[16, 16]}
          containerPadding={[8, 0]}
          draggableHandle=".drag-handle"
          isDraggable={canEdit}
          isResizable={canEdit}
          onLayoutChange={(_cur: Layout[], all: Layouts) => {
            const next = { lg: toItems(all.lg ?? []), md: toItems(all.md ?? []), sm: toItems(all.sm ?? []) };
            if (JSON.stringify(next) !== JSON.stringify(body.layout)) persist({ ...body, layout: next });
          }}
        >
          {body.items.map((item) => {
            const w = widgets[item.widgetId];
            if (!w) return <div key={item.i} />;
            return (
              <div key={item.i}>
                <WidgetCard
                  widget={w}
                  time={time}
                  locationIds={locationIds}
                  inputs={inputValues}
                  dashboardId={id}
                  pageFilters={pageFilters}
                  onAddFilter={(f) => onPageFiltersChange(addPageFilter(pageFilters, f))}
                  zoom={zoom}
                  onZoom={onZoomChange}
                  onBreakDown={(step) => onDrillChange({ v: DATES_V, widgetId: w.id, steps: [step] })}
                  grainsOf={grainsOf}
                  canEdit={canEdit}
                  onEdit={() => openBuilder(draftFromDefinition(w.definition), w.id)}
                  onAskEdit={canAsk && firstPack && (!w.definition.query.pack || w.definition.query.pack === firstPack) ? () => {
                    openBuilder(draftFromDefinition(w.definition), w.id);
                    setAskOpen(true);
                  } : undefined}
                  onDuplicate={() => void duplicate(w)}
                  onRemove={() => removeItem(item.i)}
                  // An update badge per widget, unless the whole dashboard came from the same listing (the banner covers it).
                  update={canUpdateCopies && outdated(w.installedFrom) && w.installedFrom!.listingId !== from?.listingId ? w.installedFrom : undefined}
                  onUpdate={() => startUpdate({ listingId: w.installedFrom!.listingId, target: { widgetId: w.id }, title: w.definition.title })}
                  onPublish={!canPublish || w.installedFrom ? undefined : () => publish({
                    kind: "widget", sourceId: w.id, title: w.definition.title, description: w.definition.description, queries: [w.definition.query],
                  })}
                />
              </div>
            );
          })}
        </Grid>
      )}

      <BreakdownPanel
        drill={drill}
        widget={drill && body.items.some((i) => i.widgetId === drill.widgetId) ? widgets[drill.widgetId] : undefined}
        onChange={onDrillChange}
        time={time}
        locationIds={locationIds}
        inputs={inputValues}
        pageFilters={pageFilters}
        canEdit={canEdit}
        onSaved={(w) => {
          addWidget(w, true);
          onDrillChange(undefined);
        }}
      />
      {canSchedule && (
        <DashboardSchedulesDialog
          open={scheduleOpen} onOpenChange={setScheduleOpen} dashboardId={id} title={body.title} locationIds={locationIds}
          staffSession={me.data!.userId.startsWith("staff:")}
        />
      )}
      {canEdit && (
        <>
          <Builder
            time={time} locationIds={locationIds} onSaved={addWidget} onAsk={canAsk ? () => setAskOpen(true) : undefined}
            dashboardInputs={inputBoxes} inputValues={inputValues}
          />
          {canAsk && <AskBar open={askOpen} onOpenChange={setAskOpen} firstPack={firstPack} />}
          {me.data && can.own && (
            <ShareDialog open={shareOpen} onOpenChange={setShareOpen} dashboardId={id} title={body.title} shares={shares} me={me.data} />
          )}
          {publishing && (
            <PublishDialog
              key={`${publishing.kind}:${publishing.sourceId}`} open={publishOpen} onOpenChange={setPublishOpen} onPublished={setJustPublished} {...publishing}
            />
          )}
          {updating && (
            <UpdateDialog key={JSON.stringify(updating.target)} open={updateOpen} onOpenChange={setUpdateOpen} {...updating} />
          )}
          <DeleteDashboardDialog
            open={deleteOpen} onOpenChange={setDeleteOpen} dashboardId={id} title={body.title}
            widgetIds={body.items.map((i) => i.widgetId)} shareCount={shares.length}
          />
        </>
      )}
    </div>
  );
}

function EmptyDashboard({ canEdit, onAdd, onAsk }: { canEdit: boolean; onAdd: () => void; onAsk?: () => void }) {
  return (
    <div className="mt-16 flex flex-col items-center text-center">
      <div className="flex size-12 items-center justify-center rounded-2xl border border-border bg-surface shadow-card">
        <LayoutGrid className="size-5 text-subtle" />
      </div>
      <h2 className="mt-4 font-semibold">Build a dashboard that fits how you work</h2>
      <p className="mt-1 max-w-md text-sm text-muted">
        Pick certified metrics, group and filter them however you like, or just ask a question in plain English.
      </p>
      {canEdit && (
        <div className="mt-5 flex gap-2">
          {onAsk && <Button onClick={onAsk}><Sparkles className="size-3.5 text-accent" /> Ask a question</Button>}
          <Button variant="primary" onClick={onAdd}><Plus className="size-3.5" /> Add widget</Button>
        </div>
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="mx-auto max-w-[1600px] px-6 pt-4">
      <Skeleton className="h-8 w-64" />
      <div className="mt-6 grid grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}
        <Skeleton className="col-span-2 h-72" />
        <Skeleton className="col-span-2 h-72" />
      </div>
    </div>
  );
}
